import { useEffect, useState } from 'react';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 9.4 — Formulario de 2FA (TOTP + backup codes).
 *
 * 3 estados de UI según `estado.enabled`:
 *   - INACTIVO: botón "Activar 2FA" que dispara enrollment (PIN re-auth).
 *   - ENROLLMENT (transitorio): muestra QR + input para código de 6 dígitos.
 *   - ACTIVO: información de estado + opción de regenerar backup codes o
 *     desactivar (ambas con PIN re-auth).
 *
 * Single-display: los backup codes en CLARO sólo aparecen UNA vez tras
 * `activar` o `regenerar`. Si la dueña cierra la modal, no hay forma de
 * verlos de nuevo — solo regenerar.
 */

type Estado = { enabled: boolean; backupCodesRestantes: number } | null;

type FaseEnrollment =
  | { tipo: 'idle' }
  | { tipo: 'pidiendo-pin' }
  | {
      tipo: 'mostrando-qr';
      qrDataUrl: string;
      otpauthUrl: string;
      secretBase32: string;
      pinActual: string;
    }
  | { tipo: 'mostrando-backup'; codes: string[] };

export function DosFactoresForm() {
  const sesion = useSessionStore((s) => s.sesion);
  const [estado, setEstado] = useState<Estado>(null);
  const [cargando, setCargando] = useState(true);
  const [fase, setFase] = useState<FaseEnrollment>({ tipo: 'idle' });
  const [pinInput, setPinInput] = useState('');
  const [codigoInput, setCodigoInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState<
    | { tipo: 'ok'; texto: string }
    | { tipo: 'error'; texto: string }
    | null
  >(null);
  const [modalDesactivar, setModalDesactivar] = useState(false);
  const [modalRegenerar, setModalRegenerar] = useState(false);

  const cargarEstado = async () => {
    if (!sesion) return;
    const res = await window.api.usuarios.totpEstado(sesion.usuarioId);
    if (res.ok) setEstado(res.data);
    setCargando(false);
  };

  useEffect(() => {
    cargarEstado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sesion?.usuarioId]);

  if (!sesion || sesion.rol !== 'dueño') {
    return (
      <div className="card max-w-md">
        <div className="flex items-center gap-2 text-slate-500">
          <ShieldCheck className="h-5 w-5" />
          <p className="text-sm">
            El segundo factor sólo aplica a usuarios con rol dueño.
          </p>
        </div>
      </div>
    );
  }

  if (cargando) return <div className="card max-w-md">Cargando…</div>;

  // -----------------------------------------------------------------------
  // FASE: mostrando backup codes (post-activar o post-regenerar)
  // -----------------------------------------------------------------------
  if (fase.tipo === 'mostrando-backup') {
    return (
      <BackupCodesPanel
        codes={fase.codes}
        onCerrar={() => {
          setFase({ tipo: 'idle' });
          cargarEstado();
        }}
      />
    );
  }

  // -----------------------------------------------------------------------
  // FASE: mostrando QR (enrollment confirmando con código TOTP)
  // -----------------------------------------------------------------------
  if (fase.tipo === 'mostrando-qr') {
    const onActivar = async () => {
      if (!/^\d{6}$/.test(codigoInput)) {
        setMensaje({ tipo: 'error', texto: 'Escribe las 6 cifras que aparecen en tu celular.' });
        return;
      }
      setEnviando(true);
      setMensaje(null);
      const res = await window.api.usuarios.totpActivar({
        usuarioId: sesion.usuarioId,
        pinActual: fase.pinActual,
        codigo: codigoInput,
      });
      setEnviando(false);
      if (res.ok) {
        setCodigoInput('');
        setFase({ tipo: 'mostrando-backup', codes: res.data.backupCodes });
      } else {
        setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
      }
    };

    return (
      <div className="card max-w-md space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold">Paso 2 de 3 — Conecta tu celular</h2>
        </div>

        {/*
          Sprint 159 — En palabras de mostrador. La dueña no es usuaria de computadora: "2FA",
          "app autenticadora" y "escanea el QR" eran tres palabras que no conoce en una sola
          pantalla. Los pasos son los mismos del video-guía que se le manda por WhatsApp.
        */}
        <ol className="list-decimal pl-5 text-base text-slate-800 space-y-2">
          <li>En tu celular, abre <strong>Google Authenticator</strong>.</li>
          <li>Toca el botón <strong>+</strong> y elige <strong>«Escanear un código QR»</strong>.</li>
          <li>Apunta la cámara del celular a <strong>este cuadrito</strong>.</li>
          <li>Escribe abajo el <strong>número de 6 cifras</strong> que aparece en tu celular.</li>
        </ol>

        <div className="flex justify-center">
          <img
            src={fase.qrDataUrl}
            alt="Cuadrito para apuntar con la cámara del celular"
            className="h-48 w-48 rounded-lg border border-slate-200 bg-white p-2"
          />
        </div>

        <details className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
          <summary className="cursor-pointer text-slate-600">
            ¿La cámara no lo lee? Escribe esta clave en la aplicación
          </summary>
          <p className="mt-2 break-all font-mono text-slate-800">
            {fase.secretBase32}
          </p>
        </details>

        <div>
          <label className="label" htmlFor="codigoTotp">
            Número que aparece en tu celular
          </label>
          <p className="text-xs text-slate-600 mb-1">
            Cambia cada 30 segundos: escribe el que se vea en ese momento.
          </p>
          <input
            id="codigoTotp"
            type="text"
            inputMode="numeric"
            maxLength={6}
            autoFocus
            className="input font-mono text-center text-2xl tracking-[0.4em]"
            value={codigoInput}
            onChange={(e) => setCodigoInput(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
          />
        </div>

        {mensaje && (
          <div
            className={`rounded-lg px-3 py-2 text-sm ${
              mensaje.tipo === 'ok'
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-red-50 text-red-700'
            }`}
            role="alert"
          >
            {mensaje.texto}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => {
              setFase({ tipo: 'idle' });
              setCodigoInput('');
              setMensaje(null);
            }}
            className="btn-ghost flex-1"
            disabled={enviando}
          >
            Cancelar
          </button>
          <button
            onClick={onActivar}
            disabled={enviando || codigoInput.length !== 6}
            className="btn-primary flex-1"
          >
            {enviando ? 'Activando…' : 'Listo, activar'}
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // FASE: pidiendo PIN para iniciar enrollment
  // -----------------------------------------------------------------------
  if (fase.tipo === 'pidiendo-pin') {
    const onGenerar = async () => {
      if (!/^\d{4}$/.test(pinInput)) {
        setMensaje({ tipo: 'error', texto: 'PIN inválido.' });
        return;
      }
      setEnviando(true);
      setMensaje(null);
      const res = await window.api.usuarios.totpGenerarSecret({
        usuarioId: sesion.usuarioId,
        pinActual: pinInput,
      });
      setEnviando(false);
      if (res.ok) {
        setFase({
          tipo: 'mostrando-qr',
          qrDataUrl: res.data.qrDataUrl,
          otpauthUrl: res.data.otpauthUrl,
          secretBase32: res.data.secretBase32,
          pinActual: pinInput,
        });
        setPinInput('');
      } else {
        setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
      }
    };

    return (
      <div className="card max-w-md space-y-4">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold">Paso 1 de 3 — Escribe tu PIN</h2>
        </div>
        <p className="text-base text-slate-700">
          Escribe el mismo PIN con el que entras al sistema.
        </p>

        <div>
          <label className="label" htmlFor="pinReauth">
            Tu PIN
          </label>
          <input
            id="pinReauth"
            type="password"
            inputMode="numeric"
            maxLength={4}
            autoFocus
            className="input font-mono text-center text-2xl tracking-[0.5em]"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
            placeholder="••••"
          />
        </div>

        {mensaje && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {mensaje.texto}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => {
              setFase({ tipo: 'idle' });
              setPinInput('');
              setMensaje(null);
            }}
            className="btn-ghost flex-1"
            disabled={enviando}
          >
            Cancelar
          </button>
          <button
            onClick={onGenerar}
            disabled={enviando || pinInput.length !== 4}
            className="btn-primary flex-1"
          >
            {enviando ? 'Generando…' : 'Continuar'}
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // ESTADO ACTIVO: información + opciones (desactivar / regenerar)
  // -----------------------------------------------------------------------
  if (estado?.enabled) {
    return (
      <>
        <div className="card max-w-md space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-bold">Entrar con tu celular</h2>
            <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
              ENCENDIDO
            </span>
          </div>

          <p className="text-base text-slate-700">
            Tu cuenta está protegida: al entrar, después de tu PIN, el sistema te pide el número
            que aparece en <strong>Google Authenticator</strong> en tu celular.
          </p>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Claves de emergencia que te quedan:</span>
              <span
                className={`font-mono font-bold ${
                  estado.backupCodesRestantes <= 1
                    ? 'text-red-600'
                    : estado.backupCodesRestantes <= 2
                    ? 'text-amber-600'
                    : 'text-emerald-700'
                }`}
              >
                {estado.backupCodesRestantes} / 5
              </span>
            </div>
            {estado.backupCodesRestantes <= 1 && (
              <p className="mt-1 text-xs text-red-700">
                ⚠ Te quedan muy pocas. Saca 5 nuevas.
              </p>
            )}
          </div>

          {mensaje && (
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                mensaje.tipo === 'ok'
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-red-50 text-red-700'
              }`}
              role="alert"
            >
              {mensaje.texto}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <button
              onClick={() => {
                setModalRegenerar(true);
                setPinInput('');
                setMensaje(null);
              }}
              className="btn-secondary"
            >
              <RefreshCw className="h-4 w-4" />
              Sacar 5 claves de emergencia nuevas
            </button>
            <button
              onClick={() => {
                setModalDesactivar(true);
                setPinInput('');
                setMensaje(null);
              }}
              className="btn-ghost text-red-600"
            >
              <ShieldOff className="h-4 w-4" />
              Apagar
            </button>
          </div>
        </div>

        {modalDesactivar && (
          <ConfirmacionPinModal
            titulo="Apagar la entrada con tu celular"
            descripcion="Tu cuenta quedará protegida sólo con el PIN. Escríbelo para confirmar."
            confirmar="Apagar"
            destructivo
            pinInput={pinInput}
            setPinInput={setPinInput}
            enviando={enviando}
            onCancelar={() => {
              setModalDesactivar(false);
              setPinInput('');
            }}
            onConfirmar={async () => {
              if (!/^\d{4}$/.test(pinInput)) {
                setMensaje({ tipo: 'error', texto: 'PIN inválido.' });
                return;
              }
              setEnviando(true);
              const res = await window.api.usuarios.totpDesactivar({
                usuarioId: sesion.usuarioId,
                pinActual: pinInput,
              });
              setEnviando(false);
              if (res.ok) {
                setModalDesactivar(false);
                setPinInput('');
                setMensaje({ tipo: 'ok', texto: 'Listo: ya no se te pedirá el número del celular.' });
                cargarEstado();
              } else {
                setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
              }
            }}
          />
        )}

        {modalRegenerar && (
          <ConfirmacionPinModal
            titulo="Sacar claves de emergencia nuevas"
            descripcion="Las 5 claves que tienes anotadas dejarán de servir. Escribe tu PIN para confirmar."
            confirmar="Generar nuevos"
            pinInput={pinInput}
            setPinInput={setPinInput}
            enviando={enviando}
            onCancelar={() => {
              setModalRegenerar(false);
              setPinInput('');
            }}
            onConfirmar={async () => {
              if (!/^\d{4}$/.test(pinInput)) {
                setMensaje({ tipo: 'error', texto: 'PIN inválido.' });
                return;
              }
              setEnviando(true);
              const res = await window.api.usuarios.totpRegenerarBackup({
                usuarioId: sesion.usuarioId,
                pinActual: pinInput,
              });
              setEnviando(false);
              if (res.ok) {
                setModalRegenerar(false);
                setPinInput('');
                setFase({ tipo: 'mostrando-backup', codes: res.data.backupCodes });
              } else {
                setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
              }
            }}
          />
        )}
      </>
    );
  }

  // -----------------------------------------------------------------------
  // ESTADO INACTIVO: invitación a activar
  // -----------------------------------------------------------------------
  return (
    <div className="card max-w-md space-y-4">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-slate-400" />
        <h2 className="text-lg font-bold">Entrar con tu celular</h2>
        <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
          APAGADO
        </span>
      </div>

      <p className="text-base text-slate-700">
        Además de tu PIN, el sistema te pedirá un número que aparece en tu celular. Así, aunque
        alguien sepa tu PIN, <strong>no puede entrar sin tu teléfono</strong>.
      </p>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>
          Antes de empezar: instala en tu celular la aplicación gratuita{' '}
          <strong>Google Authenticator</strong> (está en la Play Store) y ten a la mano papel y pluma.
        </p>
      </div>

      <button
        onClick={() => {
          setFase({ tipo: 'pidiendo-pin' });
          setMensaje(null);
        }}
        className="btn-primary w-full"
      >
        <Shield className="h-4 w-4" />
        Activar con mi celular
      </button>
    </div>
  );
}

/**
 * Panel de single-display de backup codes. Se muestra UNA vez al activar o
 * regenerar. Diseño: lista monoespacio + botón copiar al portapapeles.
 */
function BackupCodesPanel({
  codes,
  onCerrar,
}: {
  codes: string[];
  onCerrar: () => void;
}) {
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin clipboard: silencioso. La dueña puede transcribir manualmente.
    }
  };

  return (
    <div className="card max-w-md space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-emerald-600" />
        <h2 className="text-lg font-bold">Paso 3 de 3 — Tus 5 claves de emergencia</h2>
      </div>

      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>
          <strong>Cópialas a mano en un papel ahora</strong> y guárdalo lejos del celular. Si un día
          pierdes el teléfono, con una de ellas puedes entrar. Cada una sirve una sola vez y
          <strong> no volverán a aparecer</strong>.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <ul className="grid grid-cols-1 gap-1 font-mono text-lg tracking-wider">
          {codes.map((c, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="text-slate-400 text-xs w-4">{i + 1}.</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex gap-2">
        <button onClick={copiar} className="btn-secondary flex-1">
          {copiado ? (
            <>
              <Check className="h-4 w-4" />
              Copiadas
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copiar
            </>
          )}
        </button>
        <button onClick={onCerrar} className="btn-primary flex-1">
          Ya las anoté
        </button>
      </div>
    </div>
  );
}

function ConfirmacionPinModal({
  titulo,
  descripcion,
  confirmar,
  destructivo,
  pinInput,
  setPinInput,
  enviando,
  onCancelar,
  onConfirmar,
}: {
  titulo: string;
  descripcion: string;
  confirmar: string;
  destructivo?: boolean;
  pinInput: string;
  setPinInput: (v: string) => void;
  enviando: boolean;
  onCancelar: () => void;
  onConfirmar: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-sm space-y-4">
        <h3 className="text-lg font-bold">{titulo}</h3>
        <p className="text-sm text-slate-600">{descripcion}</p>
        <div>
          <label className="label" htmlFor="pinModalReauth">
            PIN actual
          </label>
          <input
            id="pinModalReauth"
            type="password"
            inputMode="numeric"
            maxLength={4}
            autoFocus
            className="input font-mono text-center text-2xl tracking-[0.5em]"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
            placeholder="••••"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onCancelar} className="btn-ghost flex-1" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={onConfirmar}
            disabled={enviando || pinInput.length !== 4}
            className={destructivo ? 'btn-danger flex-1' : 'btn-primary flex-1'}
          >
            {enviando ? 'Procesando…' : confirmar}
          </button>
        </div>
      </div>
    </div>
  );
}
